import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import type { ThoughtChainItemType } from '@ant-design/x';
import type { ReactNode } from 'react';
import type { ChatStreamActivity } from '../../interfaces/ChatStreamActivity';

export type ThoughtStatus = ThoughtChainItemType['status'];

type Translate = (key: string, options?: Record<string, unknown>) => string;

function asDisplayString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function paramDetail(t: Translate, key: string, value: unknown): string {
  const text = asDisplayString(value).trim();
  if (!text) return '';
  return t(`chat.stream.param.${key}`, {
    value: text,
    query: text,
    pattern: text,
    filename: text,
    expression: text,
    count: text,
    reason: text,
    intent: text,
    defaultValue: '',
  });
}

/** Build i18n interpolation bag from activity params / resultSummary. */
export function streamActivityI18nOptions(
  t: Translate,
  activity: ChatStreamActivity,
): Record<string, unknown> {
  const params = activity.params ?? {};
  const summary = activity.resultSummary ?? {};
  const query = params.query;
  const pattern = params.pattern;
  const filename = params.filename ?? params.file_name ?? params.fileName;
  const expression = params.expression;
  const candidateCount = params.candidate_count ?? params.candidateCount;
  const count = summary.count ?? params.count;
  const reasonRaw = asDisplayString(params.reason).trim();
  const reasonLabel = reasonRaw
    ? t(`chat.stream.reasons.${reasonRaw}`, { defaultValue: reasonRaw })
    : '';
  // Spec §4 routing: ResultSummary={"intent": ...} on Phase=done.
  const intentRaw = asDisplayString(summary.intent ?? params.intent).trim();
  const intentLabel = intentRaw
    ? t(`chat.stream.tools.${intentRaw}`, { defaultValue: intentRaw })
    : '';

  return {
    query: asDisplayString(query),
    pattern: asDisplayString(pattern),
    filename: asDisplayString(filename),
    expression: asDisplayString(expression),
    count: asDisplayString(count),
    candidate_count: asDisplayString(candidateCount),
    reason: reasonLabel || reasonRaw,
    intent: intentLabel || intentRaw,
    queryDetail: paramDetail(t, 'query', query),
    patternDetail: paramDetail(t, 'pattern', pattern),
    filenameDetail: paramDetail(t, 'filename', filename),
    expressionDetail: paramDetail(t, 'expression', expression),
    countDetail: paramDetail(t, 'count', count),
    candidateCountDetail: paramDetail(t, 'candidateCount', candidateCount),
    reasonDetail: reasonLabel ? paramDetail(t, 'reason', reasonLabel) : '',
    intentDetail: intentLabel ? paramDetail(t, 'intent', intentLabel) : '',
  };
}

export function getThoughtStatusIcon(
  status: ThoughtStatus,
  options?: { pausedWaiting?: boolean },
): ReactNode {
  if (options?.pausedWaiting) {
    return <ClockCircleOutlined />;
  }
  switch (status) {
    case 'success':
      return <CheckCircleOutlined />;
    case 'error':
      return <InfoCircleOutlined />;
    case 'loading':
      return <LoadingOutlined />;
    case 'abort':
      return <MinusCircleOutlined />;
    default:
      return undefined;
  }
}

/** Map activity phase → Ant Design X ThoughtChain status. */
export function activityStatus(phase: string | null | undefined): ThoughtStatus {
  switch (phase) {
    case 'done':
      return 'success';
    case 'error':
      return 'error';
    case 'abort':
    case 'cancel':
    case 'cancelled':
      return 'abort';
    default:
      return 'loading';
  }
}

export function isPausedWaitingActivity(activity: ChatStreamActivity): boolean {
  return activity.tool === 'paused' && activity.phase !== 'done';
}

/** Mark unfinished tool steps as done (stream end / message Done). */
export function closeOpenStreamActivities(activities: ChatStreamActivity[]): ChatStreamActivity[] {
  let changed = false;
  const next = activities.map((activity) => {
    if (activity.phase === 'done') {
      return activity;
    }
    changed = true;
    return { ...activity, phase: 'done' as const };
  });
  return changed ? next : activities;
}

function streamActivityDescription(t: Translate, activity: ChatStreamActivity): string {
  const phaseKey = activity.phase === 'done' ? 'done' : 'start';
  const options = streamActivityI18nOptions(t, activity);
  return t(`chat.stream.descriptions.${activity.tool}.${phaseKey}`, {
    ...options,
    defaultValue: t(`chat.stream.descriptions.unknown.${phaseKey}`),
  });
}

/** AIGrant-parity ThoughtChain items from streamActivities / streamActivity. */
export function toThoughtChainItems(
  t: Translate,
  activities: ChatStreamActivity[],
): ThoughtChainItemType[] {
  return activities.map((activity, index) => {
    const title = t(`chat.stream.tools.${activity.tool}`, {
      defaultValue: t('chat.stream.tools.unknown'),
    });
    const status = activityStatus(activity.phase);
    const pausedWaiting = isPausedWaitingActivity(activity);
    const isLoading = status === 'loading';
    return {
      key: `${activity.tool}-${index}`,
      title,
      description: streamActivityDescription(t, activity),
      status,
      icon: getThoughtStatusIcon(status, { pausedWaiting }),
      // Spec §4.1: paused is a queue wait, not a short spinner blink.
      blink: isLoading && !pausedWaiting,
    };
  });
}
